import React, { useEffect, useMemo, useState } from 'react';

import CombinedClientScopeFilters from './CombinedClientScopeFilters';
import useCombinedReportScope from './useCombinedReportScope';
import { fetchCombinedProfitLoss, resolveCombinedReportError } from './combinedReportUtils';
import '../profit-loss-account/ProfitLossAccount.css';
import '../consolidated-reports/ConsolidatedReports.css';

const apiUrl = process.env.REACT_APP_API_URL;

const formatAmount = (value) => `₹ ${Number(value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const CombinedProfitLoss = () => {
  const {
    financialYear,
    clientOptions,
    clientsLoading,
    clientScopeMode,
    selectedClientIds,
    effectiveClientIds,
    scopeSummary,
    selectionPreview,
    onScopeModeChange,
    onToggleClientId,
  } = useCombinedReportScope();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!financialYear || effectiveClientIds.length === 0) {
      setData(null);
      setError('');
      setLoading(false);
      return;
    }

    let ignore = false;
    setLoading(true);

    fetchCombinedProfitLoss(apiUrl, effectiveClientIds, financialYear, {
      allAccessibleClients: clientScopeMode === 'all',
    })
      .then((response) => {
        if (ignore) {
          return;
        }
        setData(response);
        setError('');
      })
      .catch((requestError) => {
        if (!ignore) {
          setError(resolveCombinedReportError(requestError, 'Unable to load the combined profit and loss report.'));
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [clientScopeMode, effectiveClientIds, financialYear]);

  const expenseSections = useMemo(() => Object.entries(data?.Expenses || {}), [data]);
  const revenueSections = useMemo(() => Object.entries(data?.Revenue || {}), [data]);

  return (
    <div className="profit-loss-page combined-report-page">
      <div className="combined-report-header">
        <p className="consolidated-reports-eyebrow">Combined Reports</p>
        <h2>Combined Profit &amp; Loss</h2>
        <p className="financial-page-subtitle">Aggregate revenue and expense blocks across all accessible clients or a selected subset without leaving the combined reporting section.</p>
      </div>

      <CombinedClientScopeFilters
        clientOptions={clientOptions}
        clientsLoading={clientsLoading}
        clientScopeMode={clientScopeMode}
        selectedClientIds={selectedClientIds}
        effectiveClientIds={effectiveClientIds}
        financialYear={financialYear}
        onScopeModeChange={onScopeModeChange}
        onToggleClientId={onToggleClientId}
      />

      {!financialYear ? <p className="financial-message">Select a financial year from the header to load the combined report.</p> : null}
      {financialYear && clientOptions.length > 0 && effectiveClientIds.length === 0 ? <p className="financial-message">Choose one or more client IDs to load the combined report.</p> : null}
      {loading && <p className="financial-message">Loading combined profit and loss...</p>}
      {error && <p className="financial-message financial-message--error">{error}</p>}

      {data ? (
        <>
          <section className="consolidated-reports-stat-grid">
            <article className="consolidated-reports-stat-card">
              <span>Scope</span>
              <strong>{scopeSummary}</strong>
            </article>
            <article className="consolidated-reports-stat-card">
              <span>Total Revenue</span>
              <strong>{formatAmount(data.totalRevenue)}</strong>
            </article>
            <article className="consolidated-reports-stat-card">
              <span>Total Expense</span>
              <strong>{formatAmount(data.totalExpense)}</strong>
            </article>
            <article className="consolidated-reports-stat-card consolidated-reports-stat-card--accent">
              <span>Net Result</span>
              <strong>{formatAmount(Number(data.totalRevenue ?? 0) - Number(data.totalExpense ?? 0))}</strong>
            </article>
          </section>

          <p className="combined-report-caption">Included client IDs: {selectionPreview}</p>

          <div className="table-container">
            <table className="profit-loss-table">
              <thead>
                <tr>
                  <th>Expenses</th>
                  <th>Amount</th>
                  <th>Revenue</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: Math.max(expenseSections.length, revenueSections.length) }).map((_, sectionIndex) => {
                  const [expenseSectionLabel, expenseRows] = expenseSections[sectionIndex] || [];
                  const [revenueSectionLabel, revenueRows] = revenueSections[sectionIndex] || [];
                  const expenseEntries = Object.entries(expenseRows || {});
                  const revenueEntries = Object.entries(revenueRows || {});
                  const rowCount = Math.max(expenseEntries.length, revenueEntries.length);

                  return (
                    <React.Fragment key={`section-${sectionIndex}`}>
                      <tr>
                        <td colSpan="2"><strong>{expenseSectionLabel || ''}</strong></td>
                        <td colSpan="2"><strong>{revenueSectionLabel || ''}</strong></td>
                      </tr>
                      {Array.from({ length: rowCount }).map((__, rowIndex) => {
                        const [expenseLabel, expenseAmount] = expenseEntries[rowIndex] || [];
                        const [revenueLabel, revenueAmount] = revenueEntries[rowIndex] || [];

                        return (
                          <tr key={`row-${sectionIndex}-${rowIndex}`}>
                            <td style={{ paddingLeft: '20px' }}>{expenseLabel || ''}</td>
                            <td style={{ paddingLeft: '20px' }}>{expenseLabel ? formatAmount(expenseAmount) : ''}</td>
                            <td style={{ paddingLeft: '20px' }}>{revenueLabel || ''}</td>
                            <td style={{ paddingLeft: '20px' }}>{revenueLabel ? formatAmount(revenueAmount) : ''}</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                <tr>
                  <td>Total Expenses</td>
                  <td>{formatAmount(data.totalExpense)}</td>
                  <td>Total Revenue</td>
                  <td>{formatAmount(data.totalRevenue)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CombinedProfitLoss;